import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, } from '@core-ui/Components/ui/dialog';
import { Input } from '@core-ui/Components/ui/input';
import { Textarea } from '@core-ui/Components/ui/textarea';
import { Button } from '@core-ui/Components/ui/button';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useIntentApi from '../hooks/useIntentApi';
import { type ApiResponse, type IntentFormDialogProps, IntentSources } from '@types';
import { logSuccess } from '@lib/logger/logSuccess';
import { logAndToast } from '@lib/logger/logAndToast';
import { cn } from '@core-ui-lib/utils.ts';
import { useTypewriter } from '@hooks/useTypewriter.ts';
import { useSessionStore } from '@features/auth';
import { logError } from '@lib/logger/logError.ts';

interface IntentFormErrors {
  title?: string;
  body?: string;
}

export const IntentFormDialog: React.FC<IntentFormDialogProps> = ({
  open,
  onOpenChange,
  mode,
  intent,
  orgId,
  onSave,
  aiScriptFeatureFlag,
}) => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<IntentFormErrors>({});
  const isViewOnly = mode === 'view';
  const { createScript, updateScript, sketchWithAI } = useIntentApi();
  const [isTyping, setIsTyping] = useState(false);
  const [aiButtonClicked, setAiButtonClicked] = useState(false);
  const [isAIGenerated, setAIGenerated] = useState(false);
  const capturePrompt = useRef<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const intentSchema = z.object({
    title: z.string().min(1, t('intentForm.Title is required')),
    body: z.string().min(1, t('intentForm.Body is required')),
  });

  useEffect(() => {
    if (intent != null) {
      setTitle(intent.title);
      setBody(intent.body);
    } else {
      setTitle('');
      setBody('');
    }
    setErrors({});
  }, [intent, open]);

  const titleByMode: Record<'create' | 'edit' | 'view', string> = {
    create: t('intentForm.New Intent'),
    edit: t('intentForm.Edit Intent'),
    view: t('intentForm.View Intent'),
  };

  const validate = useCallback((): boolean => {
    const result = intentSchema.safeParse({ title, body });
    if (!result.success) {
      const fieldErrors: IntentFormErrors = {};
      result.error.errors.forEach((e) => {
        if (e.path[0] === 'title') {
          fieldErrors.title = e.message;
        }
        if (e.path[0] === 'body') {
          fieldErrors.body = e.message;
        }
      });
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  }, [title, body, t]);

  const validateField = useCallback(
    (field: 'title' | 'body', value: string) => {
      const partial = intentSchema.pick({ [field]: true });
      const result = partial.safeParse({ [field]: value });
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? undefined : result.error.errors[0]?.message,
      }));
    },
    [intentSchema],
  );

  const handleSave = useCallback(async (): Promise<ApiResponse<any>> => {
    setIsSaving(true);
    if (orgId?.trim() == null) {
      const errorMsg = t('intentForm.toast.error.orgMissing');
      await logAndToast({
        message: errorMsg,
        source: 'IntentFormDialog',
        toast: true,
        level: 'error',
      });
      return { success: false, error: { message: errorMsg } };
    }

    if (!validate()) {
      return { success: false, error: { message: 'Validation failed' } };
    }

    let result: ApiResponse<any> = { success: false };

    if (mode === 'create') {
      result = await createScript({
        title,
        body,
        organizationId: orgId,
        source: isAIGenerated ? IntentSources.CHATGPT : IntentSources.HUMAN,
        updatedAt: new Date().toISOString(),
      });
    } else if (mode === 'edit' && intent?.id != null) {
      result = await updateScript(intent.id, { title, body });
    }

    if (result.success) {
      await logSuccess(
        mode === 'create'
          ? t('intentForm.toast.success.intentCreate')
          : t('intentForm.toast.success.intentUpdate'),
        'IntentFormDialog',
        { intentId: intent?.id },
      );
      await onSave();
      onOpenChange(false);
    } else {
      await logAndToast({
        message: result.error?.message ?? t('intentForm.toast.error.intentSave'),
        source: 'IntentFormDialog',
        context: { intentId: intent?.id },
        toast: true,
        level: 'error',
      });
    } 
    setIsSaving(false);

    return result;
  }, [
    orgId,
    validate,
    mode,
    title,
    body,
    createScript,
    updateScript,
    onSave,
    onOpenChange,
    intent,
    t,
  ]);

  async function fetchAIScript (): Promise<void> {
    setIsTyping(true);
    setAiButtonClicked(true);
    const sketch = await sketchWithAI(String(currentUser?.userId), capturePrompt.current);
    setAIGenerated(true);
    if (sketch.data === undefined) {
      // Handle error scenario: stop typing and show error message immediately
      setBody('Could not generate AI script.');
      setIsTyping(false);
      setAiButtonClicked(false);
      await logError(
        t('intentForm.toast.error.aiCreate'),
        'IntentFormDialog',
        { user: currentUser?.userId, prompt: capturePrompt.current },
        true
      );
      return;
    }
    setBody(sketch.data?.body);
    setTitle(sketch.data?.title);
  }
  
  const handleInputChange = (event: React.ChangeEvent<HTMLPreElement>): void => {

    const { target: { value } } = event;

    setBody(String(value));

    capturePrompt.current =
        (event.target as HTMLElement).innerText !== '' // Added casting to HTMLElement for innerText
          ? String((event.target as HTMLElement).innerText)
          : String(value) ?? '';
  };

  const isFormValid = useMemo(() => {
    return (
      title.trim() !== '' &&
      body.trim() !== '' &&
      errors.title == null &&
      errors.body == null
    );
  }, [title, body, errors]);

  const handleTypewriterComplete = useCallback(() => {
    setIsTyping(false);
    setAiButtonClicked(false);
  }, []);

  const typewriterText = useTypewriter(body, isTyping, handleTypewriterComplete);


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-lg md:max-xl bg-card border-border text-card-foreground rounded-md p-4 md:p-6 shadow">
        <DialogHeader>
          <DialogTitle>{titleByMode[mode]}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 px-0 md:px-2 pb-4 flex flex-col">
          <div className={mode === 'create' ? 'flex w-full items-center' : ''}>
            <div className={mode === 'create' ? 'flex-1' : ''}>
              <Input
                aria-label="Intent Name"
                placeholder={t('intentForm.Intent name')}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  validateField('title', e.target.value);
                }}
                disabled={isViewOnly}
                className={`border w-[300px] focus:outline-none focus:ring-1 disabled:opacity-100 ${ 
                  errors.title != null
                    ? '!border-error focus:!ring-error'
                    : 'border-input focus:ring-ring'
                }`}
              />
              {errors.title != null && (
                <p className="text-xs text-error mt-1" data-qa-id="intent-title-error">{errors.title}</p>
              )}
            </div>
            {mode === 'create' && aiScriptFeatureFlag && <div className="col-span-2 col-start-5 text-center">
              <Button
                data-qa-id="intent-ai-button"
                className="flex !pr-0 items-center text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-50"
                variant="ghost"
                onClick={() => {
                  void fetchAIScript();
                }}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.27A7 7 0 0 1 14 23h-4a7 7 0 0 1-6.73-4H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                </svg>
                {t('intentForm.CreateWithAI', 'Create with AI')}
              </Button>
            </div>}
          </div>
          {mode === 'create' && isTyping && aiButtonClicked ? (
            <pre  
              dangerouslySetInnerHTML={{ __html: typewriterText }}
              aria-placeholder='Writing new script...'
              className="text-[var(--purple)] wrap h-8 p-2 resize-none overflow-y-auto overflow-x-auto h-68 text-start whitespace-normal border-1 border-[var(--purple)] rounded-md"
              onInput={(e) => {
                setBody(String(e.target.value));
                validateField('body', String(e.target.value));
              }}
            >
            </pre>
          ) : (
            <div className="flex flex-col max-h-[300px] overflow-y-auto">
              <Textarea
                aria-label="Intent Body"
                placeholder={t('intentForm.Write your intent here')}
                value={body}
                onChange={(e) => {
                  handleInputChange(e);
                  validateField('body', e.target.value);
                }}
                disabled={isViewOnly}
                className={cn(
                  'h-full w-full border !resize-none focus:outline-none focus:ring-1 disabled:opacity-100 break-all',
                  errors.body != null
                    ? '!border-error focus:!ring-error'
                    : 'border-input focus:ring-ring'
                )}
              />
              {errors.body != null && (
                <p className="text-xs text-error mt-1" data-qa-id="intent-body-error">{errors.body}</p>
              )}
            </div>
          )}

          <div className="text-right">
            <span className="text-xs text-muted-foreground">
              {body.length} {t('intentForm.Characters')}
            </span>
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button
            data-qa-id="intent-cancel-button"
            variant="outline"
            className="border border-border text-muted-foreground hover:bg-muted"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {isViewOnly ? t('intentForm.Close') : t('intentForm.Cancel')}
          </Button>
          {!isViewOnly && (
            <Button
              data-qa-id={`intent-button-${mode}`}
              type="submit"
              onClick={() => {
                handleSave().catch(console.error);
              }}
              disabled={!isFormValid || isSaving}
            >
              {mode === 'create'
                ? t('intentForm.Create')
                : t('intentForm.Save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
